import * as psl from "psl";

import { COOKIE_KEYS, STORAGE_KEYS } from "./StorageKeys";
import { getLocalStorage } from "./utils/environment";

// Type definitions for discriminated union

type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];
type CookieKey = (typeof COOKIE_KEYS)[keyof typeof COOKIE_KEYS];

type SetArg =
  | { key: StorageKey; value: string }
  | { key: CookieKey; value: string };
type GetArg = { key: StorageKey } | { key: CookieKey };
type SetObjectArg<T> =
  | { key: StorageKey; value: T }
  | { key: CookieKey; value: T };
type GetObjectArg = { key: StorageKey } | { key: CookieKey };

/**
 * Manages storage operations
 */
export class StorageManager {
  private localStorage = getLocalStorage();

  // Type guards for key type
  private isCookieKey(key: unknown): key is CookieKey {
    return Object.values(COOKIE_KEYS).includes(key as CookieKey);
  }
  private isStorageKey(key: unknown): key is StorageKey {
    return Object.values(STORAGE_KEYS).includes(key as StorageKey);
  }

  /**
   * Set an item in storage (localStorage or cookie)
   */
  public setItem(arg: SetArg): void {
    if (this.isCookieKey(arg.key)) {
      this.setCookieOnApexAndSubdomains(arg.key, arg.value, {
        path: "/",
        secure:
          typeof window !== "undefined"
            ? window.location.protocol === "https:"
            : false,
        sameSite: "Lax",
      });
    } else if (this.isStorageKey(arg.key)) {
      this.localStorage.set(arg.key, arg.value);
    } else {
      throw new Error("Invalid key type for setItem");
    }
  }

  /**
   * Get an item from storage (localStorage or cookie)
   */
  public getItem(arg: GetArg): string | null {
    if (this.isCookieKey(arg.key)) {
      if (typeof document === "undefined") return null;
      const regex = new RegExp("(^| )" + arg.key + "=([^;]+)");
      const match = regex.exec(document.cookie);
      return match?.[2] ? decodeURIComponent(match[2]) : null;
    } else if (this.isStorageKey(arg.key)) {
      try {
        return this.localStorage.get(arg.key);
      } catch {
        return null;
      }
    } else {
      throw new Error("Invalid key type for getItem");
    }
  }

  /**
   * Remove an item from storage (localStorage or cookie)
   */
  public removeItem(arg: GetArg): void {
    if (this.isCookieKey(arg.key)) {
      if (typeof document === "undefined") return;
      document.cookie = `${arg.key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    } else if (this.isStorageKey(arg.key)) {
      try {
        this.localStorage.remove(arg.key);
      } catch {
        // ignore
      }
    } else {
      throw new Error("Invalid key type for removeItem");
    }
  }

  /**
   * Set an object in storage (localStorage or cookie)
   */
  public setObject<T>(arg: SetObjectArg<T>): void {
    const stringified = JSON.stringify(arg.value);
    if (this.isCookieKey(arg.key)) {
      this.setCookieOnApexAndSubdomains(arg.key, stringified, {
        path: "/",
        secure:
          typeof window !== "undefined"
            ? window.location.protocol === "https:"
            : false,
        sameSite: "Lax",
      });
    } else if (this.isStorageKey(arg.key)) {
      this.localStorage.set(arg.key, stringified);
    } else {
      throw new Error("Invalid key type for setObject");
    }
  }

  /**
   * Get an object from storage (localStorage or cookie)
   */
  public getObject<T>(arg: GetObjectArg): T | null {
    const value = this.getItem({ key: arg.key });
    if (!value) return null;
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }

  /**
   * Utility to set a cookie on both the current domain and apex domain (if different)
   * @param name Cookie name
   * @param value Cookie value
   * @param options Optional cookie options (expires, path, secure, sameSite)
   */
  private setCookieOnApexAndSubdomains(
    name: string,
    value: string,
    options: {
      expires?: Date;
      path?: string;
      secure?: boolean;
      sameSite?: "Lax" | "Strict" | "None";
    } = {},
  ): void {
    if (typeof document === "undefined" || typeof window === "undefined")
      return;
    const hostname = window.location.hostname;
    const parsed = psl.parse(hostname);
    // Type guard: check if parsed is ParsedDomain and has a string domain
    const apexDomain =
      typeof parsed === "object" &&
      "domain" in parsed &&
      typeof parsed.domain === "string" &&
      parsed.domain
        ? parsed.domain
        : hostname;
    const cookieParts = [
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
      `path=${options.path ?? "/"}`,
      options.expires ? `expires=${options.expires.toUTCString()}` : "",
      options.secure !== false ? "Secure" : "",
      options.sameSite ? `SameSite=${options.sameSite}` : "SameSite=Lax",
    ].filter(Boolean);
    // Set for current domain
    document.cookie = cookieParts.join("; ");
    // Set for apex domain if different
    if (apexDomain && apexDomain !== hostname) {
      document.cookie = cookieParts
        .concat([`domain=.${apexDomain}`])
        .join("; ");
    }
  }
}
